<template>
  <input
    v-if="$q.platform.is.ios"
    ref="pickerRef"
    v-model="localValue"
    class="picker"
    type="time"
    @input="onIosInput($event.target.value)"
  >
  <q-popup-proxy
    v-else
    ref="popupRef"
    :cover="false"
    no-parent-event
    no-refocus
    @show="onShow"
  >
    <div class="scroll-time-picker bg-white q-pa-16">
      <div class="head row no-wrap">
        <div class="col text-black flex flex-center">{{ $t('label.hour') }}</div>
        <q-separator vertical />
        <div class="col text-black flex flex-center">{{ $t('label.minute') }}</div>
        <template v-if="!is24Hour">
          <q-separator vertical />
          <div class="col text-black flex flex-center">AM/PM</div>
        </template>
      </div>
      <div class="picker-body row no-wrap cursor-pointer non-selectable q-my-8">
        <div
          class="col relative-position overflow-hidden"
          @wheel.prevent="onWheel('hour', $event)"
          @touchstart.passive="onTouchStart('hour', $event)"
          @touchend.passive="onTouchEnd('hour', $event)"
        >
          <div class="highlight-bar" />
          <div
            class="drum-list"
            :style="{ transform: `translateY(${offsetPx(hour)}px)` }"
          >
            <div
              v-for="(h, i) in hourItems"
              :key="h"
              :class="['drum-item flex flex-center', { 'text-weight-medium': i === hour }]"
              @click="hour = i"
            >
              {{ h }}
            </div>
          </div>
        </div>
        <div
          class="col relative-position overflow-hidden"
          @wheel.prevent="onWheel('minute', $event)"
          @touchstart.passive="onTouchStart('minute', $event)"
          @touchend.passive="onTouchEnd('minute', $event)"
        >
          <div class="highlight-bar" />
          <div
            class="drum-list"
            :style="{ transform: `translateY(${offsetPx(minute)}px)` }"
          >
            <div
              v-for="(m, i) in minuteItems"
              :key="m"
              :class="['drum-item flex flex-center', { 'text-weight-medium': i === minute }]"
              @click="minute = i"
            >
              {{ m }}
            </div>
          </div>
        </div>
        <div
          v-if="!is24Hour"
          class="col column gap-8 q-pt-8 q-px-4"
        >
          <div
            v-for="(item, i) in ['AM', 'PM']"
            :key="item"
            :class="['drum-item flex flex-center', {
              'selected text-weight-medium': ampm === i,
            }]"
            @click="ampm = i"
          >
            {{ item }}
          </div>
        </div>
      </div>
      <q-btn
        class="full-width shadow"
        color="positive"
        padding="8px"
        unelevated
        :label="$t('button.confirm')"
        @click="confirm"
      />
    </div>
  </q-popup-proxy>
</template>

<script setup>
import { useQuasar } from 'quasar';
import { inject, ref, watch } from 'vue';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const $q = useQuasar();

const moment = inject('moment');
const is24Hour = inject('is24Hour', undefined);

const hourStart = is24Hour ? 0 : 1;
const hourItems = [...Array(is24Hour ? 24 : 12)].map((_, i) => String(hourStart + i).padStart(2, '0'));
const minuteItems = [...Array(12)].map((_, i) => String(i * 5).padStart(2, '0'));

let touchStartY = 0;
let touchStartIdx = 0;

const pickerRef = ref(null);
const popupRef = ref(null);
const localValue = ref(null);
const hour = ref(0);
const minute = ref(0);
const ampm = ref(0);

function offsetPx(idx) {
  return (2 - idx) * 30;
}

function clamp(val, max) {
  return Math.max(0, Math.min(val, max));
}

function onWheel(col, event) {
  const dir = event.deltaY > 0 ? 1 : -1;

  if (col === 'hour') {
    hour.value = clamp(hour.value + dir, hourItems.length - 1);
  } else {
    minute.value = clamp(minute.value + dir, minuteItems.length - 1);
  }
}

function onTouchStart(col, event) {
  touchStartY = event.touches[0].clientY;
  touchStartIdx = col === 'hour' ? hour.value : minute.value;
}

function onTouchEnd(col, event) {
  const deltaY = event.changedTouches[0].clientY - touchStartY;
  const steps = -Math.round(deltaY / 30);
  const max = col === 'hour' ? hourItems.length - 1 : minuteItems.length - 1;
  const newIdx = clamp(touchStartIdx + steps, max);

  if (col === 'hour') {
    hour.value = newIdx;
  } else {
    minute.value = newIdx;
  }
}

function onShow() {
  let h = 0;
  let m = 0;

  if (props.modelValue) {
    [h, m] = props.modelValue.split(':').map(Number);
  }

  ampm.value = h >= 12 ? 1 : 0;

  if (is24Hour) {
    hour.value = h;
  } else {
    hour.value = h % 12 === 0 ? 11 : (h % 12) - 1;
  }

  minute.value = clamp(Math.round(m / 5), minuteItems.length - 1);
}

function confirm() {
  let h;

  if (is24Hour) {
    h = hour.value;
  } else {
    const h12 = hour.value + 1;

    h = ampm.value === 0 ? h12 % 12 : (h12 % 12) + 12;
  }

  const time = `${String(h).padStart(2, '0')}:${String(minute.value * 5).padStart(2, '0')}`;
  emit('update:modelValue', time);
  hide();
}

function show() {
  if ($q.platform.is.ios) {
    pickerRef.value.focus();
  } else {
    popupRef.value.show();
  }
}

function hide() {
  popupRef.value?.hide();
}

function onIosInput(value) {
  const time = moment(value, 'HH:mm');

  emit('update:modelValue', time.isValid() ? time.format('HH:mm') : '');
}

watch(() => props.modelValue, (value) => {
  localValue.value = value;
});

defineExpose({ show, hide });
</script>

<style scoped lang="scss">
.scroll-time-picker {
  font-size: 15px;
  min-width: 310px;

  .head {
    height: 34px;
  }
}

.picker-body {
  height: 180px;
}

.drum-list {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  transition: transform 0.2s ease;
  z-index: 1;
}

.highlight-bar {
  position: absolute;
  top: 60px;
  left: 4px;
  right: 4px;
  border-radius: 6px;
  height: 30px;
  pointer-events: none;
  z-index: 0;
}

.drum-item {
  border-radius: 6px;
  height: 30px;
}

.drum-item.selected,
.highlight-bar,
.head {
  background: #0000000d;
}
</style>
